import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { formatRevenue } from "../../../utils/revenueFormat";
import { useIsMobile } from "../../../utils/useIsMobile";
import { PAYMENT_META } from "../paymentMeta";

ChartJS.register(BarElement, CategoryScale, Legend, LineElement, LinearScale, PointElement, Tooltip);

const legendMarginPlugin = {
  id: "legendMargin",
  beforeInit(chart) {
    const originalFit = chart.legend.fit;
    chart.legend.fit = function () {
      originalFit.call(this);
      this.height += 12;
    };
  },
};

// 모바일: "W1 (5/1~5/7)" → ["W1", "5/1~7"] 두 줄 라벨 (좁은 폭에서 라벨끼리 겹치지 않게, 같은 달이면 끝 날짜의 월 생략)
function toMobileWeekLabel(label) {
  const match = /^(.+?)\s*\((.+)\)$/.exec(label ?? "");
  if (!match) return label;
  const [, week, range] = match;
  const sameMonth = /^(\d{1,2})\/(\d{1,2})~\1\/(\d{1,2})$/.exec(range);
  return [week, sameMonth ? `${sameMonth[1]}/${sameMonth[2]}~${sameMonth[3]}` : range];
}

function WeeklyRevenueChart({ weekly, selectedMonth, selectedPaymentMethod }) {
  const isMobile = useIsMobile();
  const monthLabel = selectedMonth
    ? `${parseInt(selectedMonth.split("-")[1])}월`
    : "";
  const weekCount = weekly.length;
  const subtitle = [
    `${monthLabel} · ${weekCount}개 주`,
    PAYMENT_META[selectedPaymentMethod]
      ? `${PAYMENT_META[selectedPaymentMethod].label}만`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const tickFormat = (v) => (v === 0 ? "₩0" : formatRevenue(v));
  const subTickFormat = (v) => {
    if (v === 0) return "₩0";
    if (v >= 1_000_000) return `₩${Math.round(v / 10000)}만`;
    return `₩${v.toLocaleString("ko-KR")}`;
  };

  const chartData = {
    labels: weekly.map((w) => (isMobile ? toMobileWeekLabel(w.weekLabel) : w.weekLabel)),
    datasets: [
      {
        label: "캠페인",
        data: weekly.map((w) => w.campaignRevenue ?? 0),
        backgroundColor: "#378ADD",
        yAxisID: "y",
      },
      {
        type: "line",
        label: "구독",
        data: weekly.map((w) => w.subscriptionRevenue ?? 0),
        borderColor: "#1D9E75",
        backgroundColor: "#1D9E75",
        pointRadius: 3,
        pointHoverRadius: 5,
        pointHitRadius: 16,
        borderWidth: 2,
        order: -1,
        yAxisID: "y1",
      },
    ],
  };

  // 모바일: 축 글자를 줄이고 모든 주의 라벨을 표시
  const mobileTickFont = isMobile ? { font: { size: 10 } } : {};

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: isMobile ? { ticks: { autoSkip: false, ...mobileTickFont } } : {},
      y: {
        min: 0,
        ticks: { callback: tickFormat, ...mobileTickFont },
      },
      y1: {
        position: "right",
        min: 0,
        ticks: { callback: subTickFormat, ...mobileTickFont },
        grid: { drawOnChartArea: false },
      },
    },
    plugins: {
      legend: { display: true, position: "top", align: "start" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatRevenue(ctx.raw)}`,
        },
      },
    },
  };

  return (
    <div className="chart-section">
      <h5>주간 매출 분해</h5>
      <p className="weekly-subtitle">{subtitle}</p>
      <div style={{ height: 240 }}>
        <Chart
          type="bar"
          key={selectedMonth + "-" + weekly.length}
          data={chartData}
          options={chartOptions}
          plugins={[legendMarginPlugin]}
        />
      </div>
    </div>
  );
}

export default WeeklyRevenueChart;
